import * as Sentry from '@sentry/vue'

import { hidesFinancialValues } from '@/helpers/financialVisibility'
import { blueprintIdOf, isBlueprintId } from '@/helpers/franchiseMerchant'
import { identifyPerson } from '@/services/analyticsIdentity'
import api from '@/store/api'

const loadMerchantUser = ({ commit, getters }) =>
  api.call('GET', `users/${getters.loggedInUserId}`).then(response => {
    commit('GET_USER', response.data)
    commit('SET_LOGGED_IN_USER', response.data)
    commit('SET_HIDE_NUMBERS', hidesFinancialValues(response.data.data.attributes))
    if (process.env.NODE_ENV === 'production') {
      Sentry.setUser({
        domain: window.location.hostname,
        id: response.data.data.attributes.id,
        username: response.data.data.attributes.handle
      })
    }

    identifyPerson(response.data.data)
  })

// SET_LOGGED_IN_USER takes an API response and keeps its data, so "no user" is an empty resource, never undefined.
const noUser = () => ({ data: {} })

// The load in flight per token kind (and, for a franchise, per active location), so callers that
// ask at once share one profile and user fetch. A switch to another location is a load of its own.
const inFlight = {}

const inFlightKey = (getters, state) =>
  getters.franchiseTokenId ? `franchise:${state.activeFranchiseMerchantId ?? ''}` : 'merchant'

const sharedWhilePending = (key, load) => {
  if (inFlight[key]) return inFlight[key]
  const pending = load().finally(() => {
    if (inFlight[key] === pending) delete inFlight[key]
  })
  inFlight[key] = pending
  return pending
}

// The profile includes the franchisees and, when the franchise has one, its menu (blueprint) as users;
// the blueprint is kept apart so it never counts as a location.
const splitProfileUsers = (franchise, included) => {
  const blueprintId = blueprintIdOf({ franchise, franchiseBlueprint: franchise.relationships?.blueprint?.data })
  const users = included.filter(object => object.type === 'user')
  return {
    blueprint: users.find(user => isBlueprintId(user.id, blueprintId)) ?? null,
    merchants: users.filter(user => !isBlueprintId(user.id, blueprintId) && !user.attributes?.franchise_blueprint)
  }
}

// The active location must be a franchisee or the franchise menu.
const isKnownLocation = (id, { merchants, blueprint }) =>
  merchants.some(merchant => merchant.id === id) || isBlueprintId(id, blueprint?.id)

const loadFranchiseUser = ({ commit, getters, state }) =>
  api.call('GET', 'franchise/profile').then(response => {
    const { data, included = [] } = response.data
    const { merchants, blueprint } = splitProfileUsers(data, included)
    commit('SET_FRANCHISE', { franchise: data, merchants, blueprint })
    if (state.activeFranchiseMerchantId && !isKnownLocation(state.activeFranchiseMerchantId, { merchants, blueprint }))
      commit('SET_ACTIVE_FRANCHISE_MERCHANT', null)
    if (state.activeFranchiseMerchantId) return loadMerchantUser({ commit, getters })
    commit('SET_LOGGED_IN_USER', noUser())
    commit('SET_HIDE_NUMBERS', false)
  })

let actions = {
  getLoggedInUser({ commit, getters, state }) {
    const load = getters.franchiseTokenId ? loadFranchiseUser : loadMerchantUser
    return sharedWhilePending(inFlightKey(getters, state), () => load({ commit, getters, state }))
  },

  setActiveFranchiseMerchant({ commit, dispatch }, id) {
    commit('RESET_OBJECTS')
    commit('SET_ACTIVE_FRANCHISE_MERCHANT', id)
    if (id) return dispatch('getLoggedInUser')
    commit('SET_LOGGED_IN_USER', noUser())
  },

  setOngoingCall({ commit }, call) {
    commit('SET_ONGOING_CALL', call)
  },
  setActivePlatform({ commit, getters }, platform) {
    if (getters.platforms.includes(platform)) {
      commit('SET_ACTIVE_PLATFORM', platform)
    }
  },
  setSignUpPropertyValues({ commit }, data) {
    commit('SIGNUP_PROPERTY_VALUES', data)
  },
  clearSignUpPropertyValues({ commit }) {
    commit('SIGNUP_PROPERTY_VALUES', null)
  }
}

export default actions
